/**
 * Listening, over the browser's own speech recognition.
 *
 * Like the speaking side, this is the platform's API rather than a service: no
 * key, no account, and on Chrome the audio does go to Google for transcription,
 * which is worth an operator knowing before they hold down a microphone in an
 * ops room. `availability()` says which situation the machine is in rather than
 * failing silently, because "the button did nothing" is the worst possible
 * report from a feature like this.
 *
 * Support is genuinely uneven: Chrome and Edge have it behind the webkit prefix,
 * Safari has it from 14.1, Firefox does not have it at all. A build that assumes
 * it exists throws on a third of desktops.
 */

type RecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
  resultIndex: number;
}

function ctor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as RecognitionCtor | null;
}

export const isSupported = (): boolean => ctor() !== null;

export interface Listener {
  stop: () => void;
}

export interface ListenHandlers {
  /** Fires as she hears, so the operator can watch the words arrive. */
  onInterim?: (text: string) => void;
  /** The finished sentence. Fires once, then listening stops. */
  onFinal: (text: string) => void;
  onError: (message: string) => void;
  onEnd?: () => void;
}

/* ── what the microphone did, for diagnosing a browser we cannot see ────── */

const LOG: string[] = [];
const LOG_MAX = 80;
const t0 = Date.now();

let sink: ((lines: string[]) => void) | null = null;
let sinkTimer = 0;

/**
 * Where the log goes besides memory: the platform, when the console is on
 * one, a few seconds after the microphone goes quiet — so a person who says
 * "she did not hear me" does not also have to find a link and paste a log.
 */
export function setVoiceLogSink(fn: ((lines: string[]) => void) | null): void {
  sink = fn;
}

/** One line of the voice log: seconds since load, then what happened. */
export function voiceLog(line: string): void {
  LOG.push(`${((Date.now() - t0) / 1000).toFixed(1).padStart(7)}s  ${line}`);
  if (LOG.length > LOG_MAX) LOG.shift();
  if (sink && typeof window !== "undefined") {
    window.clearTimeout(sinkTimer);
    sinkTimer = window.setTimeout(() => sink?.(voiceLogText().split("\n")), 4000);
  }
}

/**
 * A line that must reach the platform now, not after the microphone goes
 * quiet: a screen that crashed may be gone in four seconds.
 */
export function voiceLogNow(line: string): void {
  voiceLog(line);
  if (sink && typeof window !== "undefined") {
    window.clearTimeout(sinkTimer);
    sink(voiceLogText().split("\n"));
  }
}

/** The voice log as text, oldest first, with the browser named at the top. */
export function voiceLogText(): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  return [`browser: ${ua}`, `supported: ${ctor() !== null}`, ...LOG].join("\n");
}

/** Turns the API's terse error codes into something an operator can act on. */
function explain(code: string | undefined): string {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return "The browser refused access to the microphone. Allow it for this site and try again.";
    case "no-speech":
      return "Nothing was heard.";
    case "audio-capture":
      return "No microphone was found on this machine.";
    case "network":
      return "Speech recognition needs the network and could not reach it.";
    case "aborted":
      return "Listening stopped.";
    default:
      return `Listening failed${code ? `: ${code}` : "."}`;
  }
}

/**
 * How long she waits after the last word before taking it as the whole
 * question. Shorter and she answered people who had paused for breath.
 */
export const PAUSE_MS = 2500;
/** A room that never goes quiet still ends the question. */
const LONGEST_MS = 30000;

export function listen(handlers: ListenHandlers, lang = "en-GB"): Listener | null {
  const C = ctor();
  if (!C) {
    handlers.onError("This browser has no speech recognition. Chrome, Edge and Safari have it; Firefox does not.");
    return null;
  }

  const rec = new C();
  rec.lang = lang;
  /*
   * Continuous, so a pause for breath mid-sentence does not end the question:
   * with it off, the browser closed the sentence at the first short pause and
   * she answered half of it. The question ends on our own silence timer below
   * instead, and a room that never goes quiet is cut off at LONGEST_MS.
   */
  rec.continuous = true;
  rec.interimResults = true;
  rec.maxAlternatives = 1;

  let settled = false;
  voiceLog(`question-mic: start (${lang})`);
  (rec as unknown as { onstart: (() => void) | null }).onstart = () => voiceLog("question-mic: started");

  /*
   * The question is over once nothing new has been heard for PAUSE_MS. The
   * browser's own "final" only closes one stretch of speech, not the
   * question, so it restarts the wait like any other word.
   */
  let quiet = 0;
  let words = "";
  const take = (how: string) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(quiet);
    window.clearTimeout(longest);
    voiceLog(`question-mic: took "${words}" ${how}`);
    handlers.onFinal(words);
    try {
      rec.stop();
    } catch {
      /* already stopping */
    }
  };
  const longest = window.setTimeout(() => {
    if (words) take("at the time limit");
  }, LONGEST_MS);

  rec.onresult = (e) => {
    if (settled) return;
    let heard = "";
    for (let i = 0; i < e.results.length; i++) heard += e.results[i][0]?.transcript ?? "";
    heard = heard.replace(/\s+/g, " ").trim();
    if (!heard) return;
    handlers.onInterim?.(heard);
    if (heard === words) return;
    words = heard;
    window.clearTimeout(quiet);
    quiet = window.setTimeout(() => take("after a pause"), PAUSE_MS);
  };

  rec.onerror = (e) => {
    window.clearTimeout(quiet);
    window.clearTimeout(longest);
    settled = true;
    voiceLog(`question-mic: error ${e.error ?? "?"}`);
    /* "aborted" is what the recogniser says when it was stopped — by this
       app, to speak or to hand the microphone over, or by Safari, which does
       it whenever speech starts. It is not a broken microphone, and treating
       it as one switched every listening loop off after the first answer. */
    if (e.error === "aborted") {
      handlers.onEnd?.();
      return;
    }
    handlers.onError(explain(e.error));
  };

  rec.onend = () => {
    /* The browser closed the microphone before the pause ran out: what it
       heard is still the question. */
    if (!settled && words) take("as the microphone closed");
    window.clearTimeout(longest);
    voiceLog(`question-mic: end${settled ? "" : " (nothing heard)"}`);
    if (!settled) handlers.onError("Nothing was heard.");
    handlers.onEnd?.();
  };

  try {
    rec.start();
  } catch (err) {
    voiceLog(`question-mic: start threw ${err instanceof Error ? err.message : String(err)}`);
    handlers.onError("Listening could not start.");
    return null;
  }

  return { stop: () => rec.abort() };
}

/* ── waking on her name ───────────────────────────────────────────────── */

/**
 * Listening in the background for one word.
 *
 * This is a different thing from taking an instruction and it is worth being
 * plain about the difference: the microphone stays open. The browser asks
 * permission once and shows a recording indicator for as long as it runs, and
 * in Chrome the audio is sent away to be transcribed. An operator should be
 * able to turn that off in one press, and the setting that controls it says so
 * rather than calling it "hands-free".
 *
 * Two practical problems this solves, both of which make naive wake-word code
 * fail within a minute:
 *
 *  - **Chrome stops on its own.** Continuous recognition ends after a stretch
 *    of silence, every time. It has to be restarted, or she goes deaf after the
 *    first quiet minute and nothing says why.
 *
 *  - **A refused microphone restarts forever.** If permission is denied, every
 *    restart fails instantly, and a naive loop spins. Consecutive failures are
 *    counted and the whole thing gives up with a message rather than burning a
 *    core in the background.
 */
export interface WakeWatcher {
  stop: () => void;
}

const GIVE_UP_AFTER = 3;

export function listenForWake(
  words: readonly string[],
  onWake: (heard: string) => void,
  onError: (message: string) => void,
  lang = "en-GB",
  /*
   * Wait for the rest of the sentence before firing. For her name: "Sky,
   * what is open today" said in one breath is heard word by word, and firing
   * on the first partial result hands her "Sky, what" as the question. Not
   * for "stop", which must act the moment it is heard.
   */
  sentence = false,
): WakeWatcher | null {
  const C = ctor();
  if (!C) {
    onError("This browser has no speech recognition, so she cannot listen for her name.");
    return null;
  }

  let stopped = false;
  let failures = 0;
  let rec: SpeechRecognitionLike | null = null;
  let pending: { text: string; timer: number } | null = null;

  const fire = (text: string) => {
    if (pending) window.clearTimeout(pending.timer);
    pending = null;
    if (stopped) return;
    failures = 0;
    onWake(text.trim());
  };

  const start = () => {
    if (stopped) return;
    const r = new C();
    rec = r;
    voiceLog(`${sentence ? "name" : "stop-word"}-mic: start`);
    r.lang = lang;
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;

    r.onresult = (e) => {
      /* Safari does not always let go of a recogniser it was told to stop,
         and one left running kept hearing the room after she stopped speaking. */
      if (stopped) {
        try {
          r.abort();
        } catch {
          /* already gone */
        }
        return;
      }
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        const text = (result[0]?.transcript ?? "").toLowerCase();
        voiceLog(`${sentence ? "name" : "stop-word"}-mic: ${result.isFinal ? "heard" : "hearing"} "${text}"`);
      }
      if (!sentence) {
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const text = (e.results[i][0]?.transcript ?? "").toLowerCase();
          if (words.some((w) => text.includes(w))) {
            fire(text);
            return;
          }
        }
        return;
      }
      /* Her name, then everything said after it — across the browser's own
         "final" breaks, which fall on any pause for breath. The sentence is
         over once nothing new has been heard for PAUSE_MS. */
      let from = -1;
      for (let i = e.results.length - 1; i >= 0; i--) {
        const text = (e.results[i][0]?.transcript ?? "").toLowerCase();
        if (words.some((w) => text.includes(w))) {
          from = i;
          break;
        }
      }
      if (from < 0) return;
      let latest = "";
      for (let i = from; i < e.results.length; i++) latest += e.results[i][0]?.transcript ?? "";
      latest = latest.toLowerCase().replace(/\s+/g, " ").trim();
      if (pending?.text === latest) return;
      if (pending) window.clearTimeout(pending.timer);
      pending = { text: latest, timer: window.setTimeout(() => fire(latest), PAUSE_MS) };
    };

    r.onstart = () => {
      failures = 0;
    };

    r.onerror = (e) => {
      voiceLog(`${sentence ? "name" : "stop-word"}-mic: error ${e.error ?? "?"}`);
      /* "no-speech" is the normal state of a quiet room, not a fault. */
      if (e.error === "no-speech" || e.error === "aborted") return;
      failures += 1;
      if (failures >= GIVE_UP_AFTER) {
        stopped = true;
        onError(explain(e.error));
      }
    };

    /* Chrome ends continuous recognition after silence. Restarting is the only
       way to keep listening, and the small delay stops a tight loop when the
       restart itself fails. */
    r.onend = () => {
      voiceLog(`${sentence ? "name" : "stop-word"}-mic: end${stopped ? " (stopped)" : ", restarting"}`);
      if (stopped) return;
      window.setTimeout(start, failures > 0 ? 1500 : 250);
    };

    try {
      r.start();
    } catch {
      failures += 1;
      if (failures >= GIVE_UP_AFTER) {
        stopped = true;
        onError("Listening for her name could not start.");
      }
    }
  };

  start();
  return {
    stop: () => {
      stopped = true;
      if (pending) window.clearTimeout(pending.timer);
      pending = null;
      try {
        rec?.abort();
      } catch {
        /* already gone */
      }
    },
  };
}
